/**
 * Utility functions for safe numerical calculations
 * Handles various input formats (comma vs dot decimal separator)
 * and prevents NaN errors on mobile devices
 */

/**
 * Safely parses a value to a number, handling:
 * - null/undefined inputs
 * - Comma as decimal separator (European format: "1,5" -> 1.5)
 * - Empty strings
 * - Already numeric values
 * 
 * @param {any} value - The value to parse
 * @param {number|null} defaultValue - Default value if parsing fails (default: null)
 * @returns {number|null} - Parsed number or defaultValue if invalid
 */
export const parseSafeNumber = (value, defaultValue = null) => {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }

  // If already a number, check if it's valid
  if (typeof value === 'number') {
    return isNaN(value) ? defaultValue : value;
  }

  // Convert to string and normalize
  let strValue = String(value).trim();
  
  // Handle empty string after trim
  if (strValue === '') {
    return defaultValue;
  }

  // Replace comma with dot for European decimal format
  strValue = strValue.replace(',', '.');
  
  // Remove any non-numeric characters except dot and minus
  // This handles cases like "120 mmHg" -> "120"
  strValue = strValue.replace(/[^\d.\-]/g, '');
  
  // Parse to number
  const parsed = parseFloat(strValue);
  
  // Check for NaN
  if (isNaN(parsed)) {
    return defaultValue;
  }

  return parsed;
};

/**
 * Safely parses a value to an integer
 * 
 * @param {any} value - The value to parse
 * @param {number|null} defaultValue - Default value if parsing fails (default: null)
 * @returns {number|null} - Parsed integer or defaultValue if invalid
 */
export const parseSafeInt = (value, defaultValue = null) => {
  const num = parseSafeNumber(value, defaultValue);
  if (num === null) return defaultValue;
  return Math.round(num);
};

/**
 * Calculates IMC (Body Mass Index) safely
 * 
 * @param {any} weight - Weight in kg
 * @param {any} height - Height in cm
 * @returns {string|null} - IMC value as string with 1 decimal, or null if insufficient data
 */
export const calculateIMCSafe = (weight, height) => {
  const w = parseSafeNumber(weight);
  const h = parseSafeNumber(height);
  
  if (w === null || h === null || h === 0) {
    return null;
  }
  
  const heightInMeters = h / 100;
  const imc = w / (heightInMeters * heightInMeters);
  
  if (isNaN(imc) || !isFinite(imc)) {
    return null;
  }
  
  return imc.toFixed(1);
};

/**
 * Calculates Waist-to-Height Ratio (WHtR)
 * Returns null if waist is 0 or not available
 * 
 * @param {any} waistCircumference - Waist circumference in cm
 * @param {any} height - Height in cm
 * @returns {string|null} - WHtR value as string with 2 decimals, or null if insufficient data
 */
export const calculateWaistHeightRatioSafe = (waistCircumference, height) => {
  const waist = parseSafeNumber(waistCircumference);
  const h = parseSafeNumber(height);
  
  // If waist is 0 or null, return null (not available)
  if (waist === null || waist === 0 || h === null || h === 0) {
    return null;
  }
  
  const ratio = waist / h;
  
  if (isNaN(ratio) || !isFinite(ratio)) {
    return null;
  }
  
  return ratio.toFixed(2);
};

/**
 * Evaluates adiposity status based on waist circumference, WHtR, and IMC
 * Handles 3 cases: normal, elevated central adiposity, elevated IMC
 * When waist = 0, only evaluates IMC
 * 
 * @param {any} waistCircumference - Waist circumference in cm (0 = not available)
 * @param {any} height - Height in cm
 * @param {string} sex - 'Masculino' or 'Femenino'
 * @param {any} weight - Weight in kg (optional, for IMC calculation)
 * @returns {object|null} - Adiposity evaluation object or null
 */
export const evaluateAdiposityStatus = (waistCircumference, height, sex, weight = null) => {
  const waist = parseSafeNumber(waistCircumference);
  const h = parseSafeNumber(height);
  const w = parseSafeNumber(weight);
  
  // Check if waist measurement is not available (0 or null)
  const waistNotAvailable = waist === null || waist === 0;
  
  // If waist not available, return null - no adiposity interpretation should be shown
  // Only "Circunferencia de cintura: no disponible" will be shown
  if (waistNotAvailable) {
    return null;
  }
  
  // Full evaluation with waist measurement
  if (h === null || h === 0) {
    return null;
  }
  
  // Calculate IMC if weight and height are available
  let imcValue = null;
  let hasElevatedIMC = false;
  if (w !== null && h !== null && h > 0) {
    const heightInMeters = h / 100;
    imcValue = w / (heightInMeters * heightInMeters);
    hasElevatedIMC = imcValue >= 25; // Sobrepeso or higher
  }
  
  const whtr = waist / h;
  const whtrValue = whtr.toFixed(2);
  
  // Waist circumference risk thresholds by sex
  const waistRiskThreshold = sex === 'Masculino' ? 102 : (sex === 'Femenino' ? 88 : 95);
  const hasElevatedWaist = waist >= waistRiskThreshold;
  
  // WHtR interpretation: >= 0.50 indicates increased central adiposity
  const hasElevatedWHtR = whtr >= 0.50;
  
  let status, description, secondaryDescription, color;
  
  // Case 1: All values within healthy range
  if (!hasElevatedWHtR && !hasElevatedWaist && !hasElevatedIMC) {
    status = 'normal';
    description = 'Los indicadores de composición corporal se encuentran dentro de rangos considerados saludables.';
    secondaryDescription = null;
    color = 'text-green-700';
  }
  // Case 2: Elevated waist or WHtR (central adiposity)
  else if (hasElevatedWHtR || hasElevatedWaist) {
    status = 'elevated_central';
    description = 'Se observa adiposidad central aumentada. Este patrón se asocia a mayor riesgo cardiometabólico según evidencia científica.';
    secondaryDescription = null;
    color = 'text-amber-700';
  }
  // Case 3: Only elevated IMC (no central adiposity)
  else if (hasElevatedIMC) {
    status = 'elevated_imc';
    description = 'Se observan indicadores compatibles con exceso de adiposidad corporal.';
    secondaryDescription = 'En ausencia de enfermedad asociada, esta condición puede representar un estado de adiposidad preclínica.';
    color = 'text-amber-700';
  }
  
  return {
    status,
    description,
    secondaryDescription,
    color,
    whtr: whtrValue,
    hasElevatedWHtR,
    hasElevatedWaist,
    hasElevatedIMC,
    waistRiskThreshold,
    waistNotAvailable: false
  };
};

/**
 * Calculates heart rate zones using Karvonen formula
 * Prioritizes FC pico from stress test (PEG) over theoretical formula
 * 
 * @param {any} age - Age in years
 * @param {any} restingHR - Resting heart rate (default 70 if invalid)
 * @param {any} fcPicoPEG - Peak HR from stress test (optional, prioritized if available)
 * @returns {object} - Heart rate zones object
 */
// ============================================================================
// Zonas de intensidad — clasificación ACSM sobre % de FC de reserva (Karvonen).
//
// Fuente: ACSM's Guidelines for Exercise Testing and Prescription, tabla de
// clasificación de intensidad relativa. Bins contiguos y semiabiertos:
//   leve      [30 %, 40 %)   → se rotula "30-39 %"
//   moderada  [40 %, 60 %)   → se rotula "40-59 %"
//   vigorosa  [60 %, 90 %)   → se rotula "60-89 %"
//
// OJO: la infografía divulgativa de ACSM usa % de FC MÁXIMA (65-75 / 76-96) y
// la fórmula 220-edad. Es otra escala y no se mezcla con ésta.
//
// Mantener en paridad estricta con backend/calculations.py.
// ============================================================================
export const HRR_LIGHT_LOW = 0.30;
export const HRR_LIGHT_HIGH = 0.40;
export const HRR_MODERATE_LOW = 0.40;
export const HRR_MODERATE_HIGH = 0.60;
export const HRR_VIGOROUS_LOW = 0.60;
export const HRR_VIGOROUS_HIGH = 0.90;

export const calculateHeartRateZonesSafe = (age, restingHR = null, fcPicoPEG = null) => {
  const ageNum = parseSafeNumber(age);
  const parsedRest = parseSafeNumber(restingHR);
  const restingAssumed = parsedRest === null || parsedRest <= 0;
  const restHR = restingAssumed ? 70 : parsedRest;
  const fcPico = parseSafeNumber(fcPicoPEG);
  
  // Determine max HR with priority for PEG result
  let maxHR;
  let usedPEG = false;
  
  if (fcPico !== null && fcPico > 0) {
    maxHR = fcPico;
    usedPEG = true;
  } else if (ageNum !== null && ageNum > 0) {
    // Tanaka formula: 208 - (0.7 × age)
    maxHR = 208 - (0.7 * ageNum);
  } else {
    // Cannot calculate without age or FC pico
    return {
      maxHR: null,
      lightLow: null,
      lightHigh: null,
      moderateLow: null,
      moderateHigh: null,
      vigorousLow: null,
      vigorousHigh: null,
      restingHR: restHR,
      restingHRAssumed: restingAssumed,
      usedPEG: false,
      insufficientData: true
    };
  }

  const hrReserve = maxHR - restHR;

  // Guard defensivo: si restHR >= maxHR (FC de reposo mal tipeada), no hay zonas
  // posibles. Espejo del mismo guard en backend/calculations.py.
  if (hrReserve <= 0) {
    return {
      maxHR: Math.round(maxHR),
      lightLow: null,
      lightHigh: null,
      moderateLow: null,
      moderateHigh: null,
      vigorousLow: null,
      vigorousHigh: null,
      restingHR: restHR,
      restingHRAssumed: restingAssumed,
      usedPEG,
      insufficientData: true
    };
  }

  return {
    maxHR: Math.round(maxHR),
    lightLow: Math.round(hrReserve * HRR_LIGHT_LOW + restHR),
    lightHigh: Math.round(hrReserve * HRR_LIGHT_HIGH + restHR),
    moderateLow: Math.round(hrReserve * HRR_MODERATE_LOW + restHR),
    moderateHigh: Math.round(hrReserve * HRR_MODERATE_HIGH + restHR),
    vigorousLow: Math.round(hrReserve * HRR_VIGOROUS_LOW + restHR),
    vigorousHigh: Math.round(hrReserve * HRR_VIGOROUS_HIGH + restHR),
    restingHR: restHR,
    restingHRAssumed: restingAssumed,
    usedPEG,
    insufficientData: false
  };
};

/**
 * Calculates Double Product (Rate-Pressure Product)
 * DP = HR × SBP (heart rate × systolic blood pressure)
 * 
 * @param {any} heartRate - Heart rate in bpm
 * @param {any} systolicBP - Systolic blood pressure in mmHg
 * @returns {number|null} - Double product value or null if insufficient data
 */
export const calculateDoubleProductSafe = (heartRate, systolicBP) => {
  const hr = parseSafeNumber(heartRate);
  const sbp = parseSafeNumber(systolicBP);
  
  if (hr === null || sbp === null || hr <= 0 || sbp <= 0) {
    return null;
  }
  
  return Math.round(hr * sbp);
};

/**
 * Formats a number for display, returning a fallback string if invalid
 * 
 * @param {any} value - The value to format
 * @param {string} fallback - Fallback string if value is invalid (default: "—")
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} - Formatted number or fallback string
 */
export const formatNumberSafe = (value, fallback = '—', decimals = 0) => {
  const num = parseSafeNumber(value);
  if (num === null) return fallback;
  return decimals > 0 ? num.toFixed(decimals) : Math.round(num).toString();
};
